import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity } from '@common/types';
import { IProviderTableFilters } from '../types';

export type PersistedProviderColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialProviderColumns: PersistedProviderColumn[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'guide_certified',
    headerName: 'GUIDE Certified',
    width: 150,
    minWidth: 150,
    sortable: false,
    filterable: false,
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'region',
    headerName: 'Region',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'clinical_leader',
    headerName: 'Clinical Leader',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'supervising_physician',
    headerName: 'Supervising Physician',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'partner_name',
    headerName: 'Partner',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'practice_name',
    headerName: 'Practice',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'assigned_cms',
    headerName: 'Care Managers',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'provider_specialty',
    headerName: 'Specialty',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    minWidth: 130,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
  },
];

export const initialProviderPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

type TProviderTableStore = {
  isFiltersCardOpen: boolean;
  handleSetIsFiltersCardOpen: (isOpen: boolean) => void;
  handleToggleFiltersCard: () => void;

  filters: IProviderTableFilters;
  draftFilters: IProviderTableFilters;

  setFilters: (newFilters: Partial<IProviderTableFilters>) => void;
  setDraftFilters: (newFilters: Partial<IProviderTableFilters>) => void;

  applyDraftFilters: () => void;
  resetFilters: () => void;
  resetDraftFilters: () => void;
  syncDraftWithAppliedFilters: () => void;
  clearStore: () => void;

  appliedFiltersCount: number;
  draftFiltersCount: number;

  columns: PersistedProviderColumn[];
  setColumns: (columns: PersistedProviderColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

const getDefaultFilters = (): IProviderTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 100,
  guide_certified: '',
  state: '',
  partner_id: '',
  practice_id: '',
  status: '',
  provider_specialty: '',
  assigned_cm_count: 0,
  assigned_cms: [],
  region_ids: [],
  clinical_leader_ids: [],
  supervising_physician_ids: [],
});

type TKeyOfFilters = keyof IProviderTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: IProviderTableFilters,
  appliedFilters: IProviderTableFilters,
): number => {
  let count = 0;

  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  filtersKeyList.forEach(key => {
    if (!isEqual(initialFiltersList[key], appliedFilters[key])) {
      count += 1;
    }
  });

  return count;
};

export const useProviderTableStore = create<TProviderTableStore>()(
  persist(
    (set, _) => ({
      isFiltersCardOpen: false,

      handleSetIsFiltersCardOpen: isOpen =>
        set(() => ({
          isFiltersCardOpen: isOpen,
        })),

      handleToggleFiltersCard: () =>
        set(state => ({
          isFiltersCardOpen: !state.isFiltersCardOpen,
        })),

      filters: getDefaultFilters(),
      draftFilters: getDefaultFilters(),

      setFilters: newFilters =>
        set(state => {
          const updatedFilters = {
            ...state.filters,
            ...newFilters,
          };

          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),

      setDraftFilters: newFilters =>
        set(state => {
          const updatedDraftFilters = {
            ...state.draftFilters,
            ...newFilters,
          };

          return {
            draftFilters: updatedDraftFilters,
            draftFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedDraftFilters,
            ),
          };
        }),

      applyDraftFilters: () =>
        set(state => {
          const appliedFilters = {
            ...state.draftFilters,
            page: 1,
          };

          return {
            filters: appliedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              appliedFilters,
            ),
          };
        }),

      resetFilters: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          filters: defaults,
          draftFilters: defaults,
          appliedFiltersCount: 0,
          draftFiltersCount: 0,
        }));
      },

      resetDraftFilters: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          draftFilters: defaults,
          draftFiltersCount: 0,
        }));
      },

      syncDraftWithAppliedFilters: () =>
        set(state => {
          const preservedFilters = {
            ...getDefaultFilters(),
            sort_by: state.filters.sort_by,
            sort_order: state.filters.sort_order,
          };

          return {
            filters: preservedFilters,
            draftFilters: preservedFilters,
            appliedFiltersCount: 0,
            draftFiltersCount: 0,
            isFiltersCardOpen: false,
          };
        }),

      clearStore: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          filters: defaults,
          draftFilters: defaults,
          appliedFiltersCount: 0,
          draftFiltersCount: 0,
          isFiltersCardOpen: false,
        }));
      },

      appliedFiltersCount: 0,
      draftFiltersCount: 0,

      columns: initialProviderColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialProviderPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialProviderColumns,
          pinnedColumns: initialProviderPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'provider-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TProviderTableStore &
            Pick<IProviderTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: IProviderTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
          draftFilters: mergedFilters,
        };
      },
    },
  ),
);
